/**
 * Handles the dynamic loading and display of images in a zoom dialog.
 * This module should be initialized once on DOMContentLoaded.
 * It uses event delegation to handle clicks on zoom buttons that may be
 * added to the page dynamically (e.g., via Barba.js).
 */

// To store the element that opened the dialog, for returning focus.
let triggerElement = null;
// To manage aborting fetch requests if the dialog is closed prematurely.
let abortController = null;

// Private function to handle closing the dialog
function closeDialog(dialog) {
    const dialogImage = dialog.querySelector('img');
    const dialogFineprint = dialog.querySelector('.zoom-dialog__fineprint');

    // If a fetch request is in flight, abort it to save bandwidth.
    if (abortController) {
        abortController.abort();
    }

    dialog.setAttribute('aria-hidden', 'true');

    // Return focus to the element that opened the dialog BEFORE hiding it.
    // This prevents the "descendant retained focus" accessibility error.
    if (triggerElement) {
        triggerElement.setAttribute('aria-expanded', 'false');
        triggerElement.focus();
        triggerElement = null; // Clear the reference
    }
    // Clear content to prevent flash of old image
    if (dialogImage) {
        dialogImage.setAttribute('src', '');
        dialogImage.setAttribute('alt', '');
    }
    if (dialogFineprint) {
        dialogFineprint.textContent = '';
    }
    dialog.classList.remove('loading');
}

// The main initialization function to be exported
export function initializeImageZoom() {
    const dialog = document.querySelector('#zoom-image-dialog');
    // If the dialog isn't on the page at all, we don't need to do anything.
    if (!dialog) {
        return;
    }

    const closeButton = dialog.querySelector('.zoom-dialog__close');

    // If the dialog is incomplete, log an error and exit.
    if (!dialog.querySelector('img') || !closeButton) {
        console.error('Zoom dialog is missing required elements (img, .zoom-dialog__close).');
        return;
    }

    // --- Event Listeners ---

    // 1. Open dialog on zoom button click (using event delegation)
    document.body.addEventListener('click', async (e) => {
        const button = e.target.closest('.media__zoom-open');
        if (!button) {
            return;
        }

        e.preventDefault();
        triggerElement = button; // Store the element that opened the dialog
        button.setAttribute('aria-expanded', 'true');

        const imageId = button.dataset.zoomId;
        const fineprint = button.dataset.fineprint || '';

        if (!imageId) {
            return;
        }

        // If a previous fetch is running, abort it before starting a new one.
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();

        dialog.classList.add('loading');
        dialog.setAttribute('aria-hidden', 'false');

        const formData = new FormData();
        formData.append('action', 'get_zoom_image_data');
        formData.append('image_id', imageId);
        formData.append('_ajax_nonce', window.app.nonce);

        try {
            const response = await fetch(window.app.ajaxurl, {
                method: 'POST',
                body: formData,
                signal: abortController.signal, // Pass the signal to the fetch request
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                const dialogImage = dialog.querySelector('img');
                const dialogFineprint = dialog.querySelector('.zoom-dialog__fineprint');

                // Wait for the image to fully load before removing the loading spinner
                // and setting focus. This prevents a flash of an empty container.
                await new Promise((resolve, reject) => {
                    dialogImage.onload = resolve;
                    dialogImage.onerror = reject; // This will be caught by the outer catch block
                    dialogImage.setAttribute('src', result.data.url);
                    dialogImage.setAttribute('alt', result.data.alt);
                    if (dialogFineprint) {
                        dialogFineprint.textContent = fineprint;
                    }
                });

                dialog.classList.remove('loading');
                // After content is loaded, move focus into the dialog for accessibility.
                closeButton.focus();
            } else {
                throw new Error(result.data.message || 'API returned success:false');
            }
        } catch (error) {
            // Don't log an error to the console if it was a user-initiated abort.
            if (error.name === 'AbortError') {
                console.log('Zoom image fetch aborted by user.');
                return;
            }
            console.error('Failed to load zoom image:', error);
            closeDialog(dialog);
            dialog.classList.remove('loading'); // Ensure loading class is removed on any error
        }
    });

    // 2. Close dialog via the close button
    closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        closeDialog(dialog);
    });

    // 3. Close dialog on escape key press
    document.addEventListener('keydown', (e) => {
        if (e.key === "Escape" && dialog.getAttribute('aria-hidden') === 'false') {
            closeDialog(dialog);
        }
    });
}